import React, { ReactElement, ReactNode } from 'react';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query';
import {
  Cd4peApiError,
  CodeProjectDetailsV1,
  ListPEIntegrationsResponseV2,
  ListVcsBranchesResponseV1,
  ListVcsCommitsResponseV1,
  ListPEIntegrationEnvironmentsResponseV2,
  ListControlReposResponseV1,
  VcsCommitV1,
  PEIntegrationV2,
  PEIntegrationEnvironmentV2,
  ListDeploymentPoliciesResponseV1,
} from '@utils/api/cd4pe';
import {
  Text,
  Link as PdsLink,
  TooltipHoverArea,
  Form,
} from '@puppet/react-components';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import useVcsLinks from '@hooks/useVcsLinks';

export type SelectOptions = {
  label: string | ReactElement | ReactNode;
  value: string | [];
}[];

export const getLoadingOption = (
  t: TFunction<'codeDelivery'>,
): SelectOptions => [
  {
    label: t('viewPipeline.form.field.select.searching'),
    value: [],
  },
];

export const getNoResultsOption = (
  t: TFunction<'codeDelivery'>,
): SelectOptions => [
  {
    label: t('viewPipeline.form.field.select.noResults'),
    value: [],
  },
];

export const formFieldValidator =
  ({ options, errorString }: { options: SelectOptions; errorString: string }) =>
  (value: string) => {
    if (value !== '' && !options?.some((option) => option.value === value)) {
      return errorString;
    }

    return false;
  };

export const getBranchOptions = ({
  query,
  fieldValue,
  t,
}: {
  query: UseQueryResult<ListVcsBranchesResponseV1, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  if (fieldValue !== '' && query?.data?.vcsBranches?.length === 0) {
    if (query.isFetching) {
      return getLoadingOption(t);
    }

    return getNoResultsOption(t);
  }

  return (
    query?.data?.vcsBranches?.map((b) => ({
      value: b.name,
      label: b.name,
    })) ?? []
  );
};

export const getBranchField = ({
  query,
  fieldValue,
  t,
  ...rest
}: {
  query: UseQueryResult<ListVcsBranchesResponseV1, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getBranchOptions({
    query,
    fieldValue,
    t,
  });

  return (
    <Form.Field
      type="autocomplete"
      label={t('viewPipeline.form.field.branch.label')}
      name="branch"
      options={options}
      placeholder={
        query.isLoading
          ? t('viewPipeline.form.field.branch.loading')
          : t('viewPipeline.form.field.branch.placeholder')
      }
      validator={formFieldValidator({
        options,
        errorString: t('viewPipeline.form.field.branch.error'),
      })}
      required
      trailingButtonIcon={' '}
      trailingButtonProps={{ loading: query.isFetching }}
      data-testid="select-branch"
      {...rest}
    />
  );
};

interface CommitOptionLabelProps {
  commit: VcsCommitV1;
  codeProject: CodeProjectDetailsV1;
  selectedBranch?: string;
}

export const CommitOptionLabel = ({
  commit,
  codeProject,
  selectedBranch,
}: CommitOptionLabelProps): ReactElement => {
  const { t } = useTranslation('codeDelivery');
  const { getBranchLink, getCommitLink } = useVcsLinks();

  return (
    <div className="view-pipeline-commit-option-label">
      <PdsLink
        className="branchLink"
        size="small"
        as="a"
        href={getBranchLink(
          codeProject.srcRepoProvider,
          codeProject.srcRepoOwner,
          codeProject.srcRepoName,
          codeProject.srcRepoId,
          selectedBranch || commit.branch,
        )}
        target="_blank"
      >
        {commit.branch}
      </PdsLink>
      <Text size="small" className="item">
        <Trans
          t={t}
          i18nKey="viewPipeline.form.field.commit.sha"
          values={{ commit: commit.sha }}
        >
          <PdsLink
            size="small"
            as="a"
            href={getCommitLink(
              codeProject.srcRepoProvider,
              codeProject.srcRepoOwner,
              codeProject.srcRepoName,
              codeProject.srcRepoId,
              commit.sha,
            )}
            target="_blank"
          >
            {commit.sha}
          </PdsLink>
        </Trans>
      </Text>
      {'committer' in commit && (
        <Text size="small" className="item">
          {t('viewPipeline.form.field.commit.committer', {
            committer: commit.committer,
          })}
        </Text>
      )}
      {commit.timestamp !== undefined && (
        <Text size="small" className="item">
          {t('viewPipeline.form.field.commit.timestamp', {
            timestamp: formatDistanceToNow(commit.timestamp),
          })}
        </Text>
      )}
      <div className="message">
        <TooltipHoverArea tooltip={commit.commitMessage} anchor="bottom">
          <Text className="item" size="small">
            {commit.commitMessage}
          </Text>
        </TooltipHoverArea>
      </div>
    </div>
  );
};

export const formatCommitsForSelect = (
  commits: UseQueryResult<ListVcsCommitsResponseV1, Cd4peApiError>,
  codeProject: CodeProjectDetailsV1,
  selectedBranch: string,
  commitSearch: string,
) => {
  return commits.data?.vcsCommits
    ?.filter((commit) => {
      if (!commitSearch) return true;

      return (
        commit.sha.toLowerCase().includes(commitSearch.toLowerCase()) ||
        commit.commitMessage?.toLowerCase().includes(commitSearch.toLowerCase())
      );
    })
    .map((commit) => ({
      value: commit.sha,
      label: (
        <CommitOptionLabel
          commit={commit}
          codeProject={codeProject}
          selectedBranch={selectedBranch}
        />
      ),
    }));
};

export const getCommitOptions = ({
  query,
  fieldValue,
  branch,
  codeProject,
  t,
}: {
  query: UseQueryResult<ListVcsCommitsResponseV1, Cd4peApiError>;
  fieldValue: string;
  branch: string;
  codeProject: CodeProjectDetailsV1;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  const fieldValueLowerCase = fieldValue.toLowerCase();
  const options: SelectOptions =
    query.data?.vcsCommits
      ?.filter((commit) => {
        if (!fieldValue) return true;

        return (
          commit.sha.toLowerCase().includes(fieldValueLowerCase) ||
          commit.commitMessage?.toLowerCase().includes(fieldValueLowerCase)
        );
      })
      .map((commit) => ({
        value: commit.sha,
        label: (
          <CommitOptionLabel
            commit={commit}
            codeProject={codeProject}
            selectedBranch={branch}
          />
        ),
      })) ?? [];

  if (fieldValue !== '' && options.length === 0) {
    return getNoResultsOption(t);
  }

  return options;
};

export const getCommitField = ({
  query,
  fieldValue,
  branch,
  codeProject,
  t,
  ...rest
}: {
  query: UseQueryResult<ListVcsCommitsResponseV1, Cd4peApiError>;
  fieldValue: string;
  branch: string;
  codeProject: CodeProjectDetailsV1;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getCommitOptions({
    query,
    fieldValue,
    branch,
    codeProject,
    t,
  });

  return (
    <Form.Field
      type="autocomplete"
      label={t('viewPipeline.form.field.commit.label')}
      name="commit"
      options={options}
      placeholder={
        query.isLoading
          ? t('viewPipeline.form.field.commit.loading')
          : t('viewPipeline.form.field.commit.placeholder')
      }
      validator={formFieldValidator({
        options,
        errorString: t('viewPipeline.form.field.commit.error'),
      })}
      required
      data-testid="select-commit"
      {...rest}
    />
  );
};

export const formatBranchesForSelect = (
  vcsBranches: UseQueryResult<ListVcsBranchesResponseV1, Cd4peApiError>,
) =>
  vcsBranches.data?.vcsBranches.map((branch) => {
    return { value: branch.name, label: branch.name };
  });

interface PEIntegrationProps {
  peIntegration: PEIntegrationV2;
}

export const PeIntegrationsOptionsLabel = ({
  peIntegration,
}: PEIntegrationProps) => {
  const { t } = useTranslation('codeDelivery');

  return (
    <div className="view-pipeline-select-option-label">
      <span>
        <TooltipHoverArea tooltip={peIntegration.name} anchor="bottom">
          {peIntegration.name}
        </TooltipHoverArea>
      </span>
      <span>
        <TooltipHoverArea
          tooltip={peIntegration.nodeClassifierUrl}
          anchor="bottom"
        >
          {t('viewPipeline.form.field.puppetEnterpriseServer', {
            puppetEnterpriseServer: peIntegration.nodeClassifierUrl,
          })}
        </TooltipHoverArea>
      </span>
    </div>
  );
};

export const getPuppetEnterpriseServerOptions = ({
  query,
  fieldValue,
  t,
}: {
  query: UseQueryResult<ListPEIntegrationsResponseV2, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  if (fieldValue !== '' && query?.data?.peIntegrations?.length === 0) {
    if (query.isFetching) {
      return getLoadingOption(t);
    }

    return getNoResultsOption(t);
  }

  return (
    query?.data?.peIntegrations?.map((peIntegration) => ({
      value: peIntegration.name,
      label: <PeIntegrationsOptionsLabel peIntegration={peIntegration} />,
    })) ?? []
  );
};

export const getPuppetEnterpriseServerField = ({
  query,
  fieldValue,
  t,
  ...rest
}: {
  query: UseQueryResult<ListPEIntegrationsResponseV2, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getPuppetEnterpriseServerOptions({
    query,
    fieldValue,
    t,
  });

  return (
    <Form.Field
      name="puppetEnterpriseServer"
      type="autocomplete"
      label={t('viewPipeline.form.field.puppetEnterpriseServer.label')}
      options={options}
      placeholder={
        query.isLoading
          ? t('viewPipeline.form.field.puppetEnterpriseServer.loading')
          : t('viewPipeline.form.field.puppetEnterpriseServer.placeholder')
      }
      required
      validator={formFieldValidator({
        options,
        errorString: t('viewPipeline.form.field.puppetEnterpriseServer.error'),
      })}
      trailingButtonIcon={' '}
      trailingButtonProps={{ loading: query.isFetching }}
      data-testid="select-pe-server"
      {...rest}
    />
  );
};

interface EnvironmentProps {
  environment: PEIntegrationEnvironmentV2;
  codeProject: CodeProjectDetailsV1;
}

export const EnvironmentOptionsLabel = ({
  environment,
  codeProject,
}: EnvironmentProps) => {
  const { t } = useTranslation('codeDelivery');
  const { getBranchLink } = useVcsLinks();

  return (
    <div className="view-pipeline-select-option-label">
      <span>
        <TooltipHoverArea tooltip={environment.name} anchor="bottom">
          {environment.name}
        </TooltipHoverArea>
      </span>
      <span>
        <TooltipHoverArea tooltip={environment.environment} anchor="bottom">
          <Trans
            t={t}
            i18nKey="viewPipeline.form.field.environment"
            values={{ environment: environment.environment }}
          >
            <PdsLink
              as="a"
              size="small"
              href={getBranchLink(
                codeProject.srcRepoProvider,
                codeProject.srcRepoOwner,
                codeProject.name,
                codeProject.srcRepoId,
                environment.environment,
              )}
              target="_blank"
            >
              {environment.environment}
            </PdsLink>
          </Trans>
        </TooltipHoverArea>
      </span>
    </div>
  );
};

export const formatEnvironmentsForSelect = (
  environments: UseQueryResult<
    ListPEIntegrationEnvironmentsResponseV2,
    Cd4peApiError
  >,
  codeProject: CodeProjectDetailsV1,
) => {
  return environments?.data?.environments?.map((env) => ({
    value: env.id,
    label: (
      <EnvironmentOptionsLabel environment={env} codeProject={codeProject} />
    ),
  }));
};

export const getEnvironmentOptions = ({
  query,
  fieldValue,
  codeProject,
  t,
}: {
  query: UseQueryResult<ListPEIntegrationEnvironmentsResponseV2, Cd4peApiError>;
  fieldValue: string;
  codeProject: CodeProjectDetailsV1;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  if (fieldValue !== '' && query?.data?.environments?.length === 0) {
    if (query.isFetching) {
      return getLoadingOption(t);
    }

    return getNoResultsOption(t);
  }

  return (
    query?.data?.environments?.map((env) => ({
      value: env.name,
      label: (
        <EnvironmentOptionsLabel environment={env} codeProject={codeProject} />
      ),
    })) ?? []
  );
};

export const getEnvironmentField = ({
  query,
  fieldValue,
  codeProject,
  t,
  ...rest
}: {
  query: UseQueryResult<ListPEIntegrationEnvironmentsResponseV2, Cd4peApiError>;
  fieldValue: string;
  codeProject: CodeProjectDetailsV1;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getEnvironmentOptions({
    query,
    fieldValue,
    codeProject,
    t,
  });

  return (
    <Form.Field
      name="environment"
      type="autocomplete"
      label={t('viewPipeline.form.field.environment.label')}
      options={options}
      placeholder={
        query.isLoading
          ? t('viewPipeline.form.field.environment.loading')
          : t('viewPipeline.form.field.environment.placeholder')
      }
      required
      validator={formFieldValidator({
        options,
        errorString: t('viewPipeline.form.field.environment.error'),
      })}
      trailingButtonIcon={' '}
      trailingButtonProps={{ loading: query.isFetching }}
      data-testid="select-pe-environment"
      {...rest}
    />
  );
};

export const formatControlReposForSelect = (
  controlRepos: UseInfiniteQueryResult<
    ListControlReposResponseV1,
    Cd4peApiError
  >,
) => {
  return controlRepos?.data?.pages[0]?.controlRepos?.map((controlRepo) => ({
    value: controlRepo.name,
    label: controlRepo.name,
  }));
};

export const getControlRepoOptions = ({
  query,
  fieldValue,
  t,
}: {
  query: UseInfiniteQueryResult<ListControlReposResponseV1, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  if (fieldValue !== '' && query?.data?.pages[0]?.controlRepos?.length === 0) {
    if (query.isFetching) {
      return getLoadingOption(t);
    }

    return getNoResultsOption(t);
  }

  return (
    query?.data?.pages[0]?.controlRepos?.map((controlRepo) => ({
      value: controlRepo.name,
      label: controlRepo.name,
    })) ?? []
  );
};

export const getControlRepoField = ({
  query,
  fieldValue,
  t,
  ...rest
}: {
  query: UseInfiniteQueryResult<ListControlReposResponseV1, Cd4peApiError>;
  fieldValue: string;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getControlRepoOptions({
    query,
    fieldValue,
    t,
  });

  return (
    <Form.Field
      name="controlRepo"
      type="autocomplete"
      options={options}
      placeholder={
        query.isLoading
          ? t('viewPipeline.form.field.controlRepo.loading')
          : t('viewPipeline.form.field.controlRepo.placeholder')
      }
      label={t('viewPipeline.form.field.controlRepo.label')}
      validator={formFieldValidator({
        options,
        errorString: t('viewPipeline.form.field.controlRepo.error'),
      })}
      footer={
        query.hasNextPage
          ? t('viewPipeline.form.field.controlRepo.footer')
          : null
      }
      required
      trailingButtonIcon={' '}
      trailingButtonProps={{ loading: query.isFetching }}
      data-testid="select-control-repo"
      selectProps={{ getPopupContainer: () => document.body }}
      {...rest}
    />
  );
};

export const getDeploymentPolicyOptions = ({
  query,
  fieldValue,
  customPolicy,
  t,
}: {
  query: UseQueryResult<ListDeploymentPoliciesResponseV1, Cd4peApiError>;
  fieldValue: string;
  customPolicy: boolean;
  t: TFunction<'codeDelivery'>;
}): SelectOptions => {
  const fieldValueLowerCase = fieldValue.toLowerCase();

  const options: SelectOptions =
    query.data?.deploymentPolicies
      ?.filter((policy) => {
        if (customPolicy !== policy.custom) return false;

        if (!fieldValue) return true;

        return (
          policy.name?.toLowerCase().includes(fieldValueLowerCase) ||
          policy.displayName?.toLowerCase().includes(fieldValueLowerCase)
        );
      })
      .map((policy) => ({
        value: policy.displayName ?? '',
        label: policy.displayName ?? '',
      })) ?? [];

  if (fieldValue !== '' && options.length === 0) {
    return getNoResultsOption(t);
  }

  return options;
};

export const getDeploymentPolicyField = ({
  query,
  fieldValue,
  customPolicy,
  t,
  ...rest
}: {
  query: UseQueryResult<ListDeploymentPoliciesResponseV1, Cd4peApiError>;
  fieldValue: string;
  customPolicy: boolean;
  t: TFunction<'codeDelivery'>;
  [key: string]: unknown;
}) => {
  const options = getDeploymentPolicyOptions({
    query,
    fieldValue,
    customPolicy,
    t,
  });

  let placeHolder;
  if (query.isFetching) {
    placeHolder = t(
      'viewPipeline.form.deployment.field.deploymentPolicy.loading',
    );
  } else if (customPolicy) {
    placeHolder = t(
      'viewPipeline.form.deployment.field.deploymentPolicy.custom.placeholder',
    );
  } else {
    placeHolder = t(
      'viewPipeline.form.deployment.field.deploymentPolicy.builtin.placeholder',
    );
  }

  return (
    <Form.Field
      type="autocomplete"
      name="deploymentPolicy"
      placeholder={placeHolder}
      options={options}
      required
      validator={formFieldValidator({
        options,
        errorString: t(
          'viewPipeline.form.deployment.field.deploymentPolicy.error',
        ),
      })}
      disabled={query.isLoading || query.isFetching || query.isError}
      trailingButtonIcon={' '}
      trailingButtonProps={{ loading: query.isFetching }}
      data-testid="select-deployment-policy"
      {...rest}
    />
  );
};
